import { Component } from "vue-property-decorator";
import { MouseDragInfo, ValidMouseDragInfo } from "@/lib/canvas/drag/drag";
import Vue from "vue";
import {
  getActiveElementPositionAndSize,
  ElementPositionAndSize,
  clearTextSelection
} from "@/lib/canvas/helpers";
import { uiStore } from "@/store/store";
import { forceNextTick } from "@/util/force-next-tick";
import _throttle from "lodash/throttle";

@Component
export class DragMixin extends Vue {
  clickedHandle: string | false = false;

  mouseDragInfo: MouseDragInfo = false;

  dragCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _elementPositionAndSize: ElementPositionAndSize,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _mouseDragInfo: ValidMouseDragInfo,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handle: string
  ) {
    console.warn("To use DragMixin you must implement dragCallback()");
  } /* how do we type this so that you can't override it in the wrong way? */

  dragEndCallback() {} /* how do we type this so that you can't override it in the wrong way? */

  dragStartCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handle: string
  ) {} /* how do we type this so that you can't override it in the wrong way? */

  dragStart(e: MouseEvent, handle?: string) {
    // e.preventDefault(); // If we don't call `preventDefault` we will continue to receive events, even if we leave the iframe
    e.stopPropagation();

    if (typeof handle !== "string") {
      console.warn(
        "To use drag mixin you must specify a handle parameter (string) as the second param of your mousedown=dragStart, i.e. mousedown=dragStart($event, 'myhandle')"
      );
      return;
    }

    uiStore.setDragMixinDragging(true);
    this.clickedHandle = handle;

    this.mouseDragInfo = {
      startX: e.clientX,
      startY: e.clientY,
      currentX: e.clientX,
      currentY: e.clientY,
      lastXChange: 0,
      lastYChange: 0,
      totalXChange: 0,
      totalYChange: 0
    };

    window.Breakdance.canvas.document.addEventListener(
      "mousemove",
      this.throttledDragging
    );
    window.Breakdance.canvas.document.addEventListener("mouseup", this.dragEnd);

    this.dragStartCallback(handle);
  }

  throttledDragging = _throttle(this.dragging, 60);

  dragging(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    requestAnimationFrame(() => this._draggingFunc(e));
  }

  _draggingFunc(e: MouseEvent) {
    const elementPositionAndSize = getActiveElementPositionAndSize();
    if (!elementPositionAndSize || !this.mouseDragInfo) return;

    this.mouseDragInfo = {
      ...this.mouseDragInfo,
      currentX: e.clientX,
      currentY: e.clientY,
      lastXChange: e.clientX - this.mouseDragInfo.currentX,
      lastYChange: e.clientY - this.mouseDragInfo.currentY,
      totalXChange: e.clientX - this.mouseDragInfo.startX,
      totalYChange: e.clientY - this.mouseDragInfo.startY
    };

    if (this.clickedHandle) {
      this.dragCallback(
        elementPositionAndSize,
        this.mouseDragInfo,
        this.clickedHandle
      );
    }

    this.positionWrapper();
    clearTextSelection();
  }

  dragEnd(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    // eslint-disable-next-line prettier/prettier
    window.Breakdance.canvas.document.removeEventListener(
      "mousemove",
      this.throttledDragging
    );
    window.Breakdance.canvas.document.removeEventListener(
      "mouseup",
      this.dragEnd
    );
    this.mouseDragInfo = false;
    this.clickedHandle = false;

    uiStore.setDragMixinDragging(false);
    this.dragEndCallback();
    clearTextSelection();
  }

  get isDragging() {
    return !!this.mouseDragInfo;
  }

  unsubscribeFromStorePositionWrapper = () => {};
  nextTickPositionsWrapper() {
    forceNextTick(this.positionWrapper);
  }

  beforeMount() {
    this.positionWrapper();

    this.unsubscribeFromStorePositionWrapper = this.$store.subscribe(
      this.nextTickPositionsWrapper
    );

    window.addEventListener("resize", this.nextTickPositionsWrapper);
  }

  beforeDestroy() {
    this.unsubscribeFromStorePositionWrapper();
    window.removeEventListener("resize", this.nextTickPositionsWrapper);
  }

  wrapperPositionStyle: { [key: string]: string } = {
    display: "none"
  };

  positionWrapper() {
    if (!uiStore.activeElementId) {
      this.wrapperPositionStyle = {
        display: "none"
      };

      return;
    }

    const elementPositionAndSize = getActiveElementPositionAndSize();
    if (!elementPositionAndSize) return;

    const { x, y, width, height } = elementPositionAndSize;

    this.wrapperPositionStyle = {
      position:
        "absolute" /* this won't work if something other than the window has position relative on it 
        maybe we could mount the overlaid ui chrome inside the body */,
      top: `${y}px`,
      left: `${x}px`,
      width: `${width}px`,
      height: `${height}px`
    };
  }
}
